import React from 'react';
import { FiTrendingUp, FiTrendingDown, FiMinus } from 'react-icons/fi';

const StatsCard = ({ 
  title, 
  value, 
  subtitle, 
  change, 
  changeType = 'neutral', // 'positive', 'negative', 'neutral'
  icon, 
  className = '',
  size = 'normal', // 'small', 'normal', 'large'
  variant = 'default', // 'default', 'primary', 'success', 'warning', 'danger'
  loading = false,
  clickable = false,
  onClick
}) => {
  const getChangeIcon = () => {
    switch (changeType) {
      case 'positive':
        return <FiTrendingUp />;
      case 'negative':
        return <FiTrendingDown />;
      default:
        return <FiMinus />;
    }
  };

  const cardClasses = [
    'stats-card',
    `stats-card--${size}`,
    `stats-card--${variant}`,
    changeType && `stats-card--${changeType}`,
    clickable && 'stats-card--clickable',
    loading && 'stats-card--loading',
    className
  ].filter(Boolean).join(' ');

  const CardContent = () => (
    <>
      {loading && (
        <div className="stats-card__loading">
          <div className="loading-skeleton"></div>
        </div>
      )}
      
      {!loading && (
        <>
          <div className="stats-card__header">
            {icon && (
              <div className="stats-card__icon">
                {React.cloneElement(icon, { className: 'icon' })}
              </div>
            )}
            <div className="stats-card__title">{title}</div>
          </div>

          <div className="stats-card__content">
            <div className="stats-card__value">
              {typeof value === 'number' ? value.toLocaleString() : value}
            </div>
            
            {subtitle && (
              <div className="stats-card__subtitle">{subtitle}</div>
            )}
            
            {change && (
              <div className={`stats-card__change stats-card__change--${changeType}`}>
                <span className="change-icon">{getChangeIcon()}</span>
                <span className="change-text">{change}</span>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );

  if (clickable && onClick) {
    return (
      <button className={cardClasses} onClick={onClick}>
        <CardContent />
      </button>
    );
  }

  return (
    <div className={cardClasses}>
      <CardContent />
    </div>
  );
};

// Componente para grid de estadísticas
const StatsGrid = ({ 
  children, 
  columns = 'auto', // 'auto', 1, 2, 3, 4, 5
  gap = 'normal', // 'small', 'normal', 'large'
  className = '' 
}) => {
  const gridClasses = [
    'stats-grid',
    `stats-grid--columns-${columns}`,
    `stats-grid--gap-${gap}`,
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={gridClasses}>
      {children}
    </div>
  );
};

// Hook para formatear valores
const useStatsFormat = () => {
  const formatCurrency = (value, currency = '$') => {
    if (typeof value !== 'number') return value;
    return `${currency}${value.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    })}`;
  };

  const formatPercentage = (value, decimals = 1) => {
    if (typeof value !== 'number') return value;
    return `${value.toFixed(decimals)}%`;
  };

  const formatNumber = (value, decimals = 0) => {
    if (typeof value !== 'number') return value;
    return value.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  const formatCompactNumber = (value) => {
    if (typeof value !== 'number') return value;
    
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toLocaleString();
  };

  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    return {
      percentage: Math.abs(change).toFixed(1) + '%',
      type: change > 0 ? 'positive' : change < 0 ? 'negative' : 'neutral',
      raw: change
    };
  };

  return {
    formatCurrency,
    formatPercentage,
    formatNumber,
    formatCompactNumber,
    calculateChange
  };
};

export { StatsCard, StatsGrid, useStatsFormat };
export default StatsCard;